// src/game/map/floorMoods.js

/**
 * FLOOR MOODS
 * ----------
 * This file is the single source of truth for floor mood definitions.
 *
 * The goal is:
 * - easy to add new moods
 * - easy to tweak values later
 * - deterministic mood selection per floor/run
 * - simple helpers for App.js and generateMap.js
 *
 * IMPORTANT:
 * Step 1 only creates this file.
 * We are NOT wiring it into App.js or generateMap.js yet.
 */

/* =============================================
 * SMALL SEEDED RNG HELPERS
 * ============================================= */

function mulberry32(seed) {
  let t = seed >>> 0;
  return function () {
    t += 0x6D2B79F5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function normalizeMoodId(value) {
  return String(value || '').trim().toLowerCase();
}

/* =============================================
 * GLOBAL DEFAULTS
 * ============================================= */

// Matches your current torch duration in App.js.
// Later, App.js can pass this in explicitly too if you prefer.
export const DEFAULT_MOOD_TORCH_DURATION = 64;

// Used when a mood doesn't define anything special for floor tiles yet.
export const DEFAULT_FLOOR_TILESET_ID = 'default';

// Floor 1 should remain a fixed handcrafted-feeling baseline.
// Randomized floor moods only start from this floor onward.
export const FLOOR_MOODS_START_FLOOR = 2;

export function isFloorMoodEnabledForFloor(floor) {
  const lvl = Math.max(1, Math.floor(Number(floor) || 1));
  return lvl >= FLOOR_MOODS_START_FLOOR;
}

/* =============================================
 * FLOOR MOOD DEFINITIONS
 * ============================================= */

/**
 * Notes on the values below:
 *
 * - "Higher / Lower density" needed concrete numbers, so I picked safe starter values.
 * - These are intentionally easy to edit later in ONE place.
 * - flatBonus means "add N more on top of normal generation"
 * - multiplier means "scale the normal generator count"
 *
 * If something feels too strong/weak later, you only change numbers here.
 */
export const FLOOR_MOODS = [
  {
    id: 'overtaken',
    name: 'Overtaken Floor',
    weight: 2,
    tilesetId: 'default',

    mapSize: { mode: 'range', min: 25, max: 35 },

    torchDuration: {
      mode: 'default',
      value: DEFAULT_MOOD_TORCH_DURATION,
    },

    layout: {
      preset: 'default',
      forceSingleRoom: false,
      noCorridors: false,
      roomSizeMultiplier: 1.0,
      roomCountMultiplier: 1.0,
      corridorDensityMultiplier: 1.0,
      corridorLengthMultiplier: 1.0,
      deadEndMultiplier: 1.0,
    },

    counts: {
      enemyMultiplier: 1.35,
      trapMultiplier: 1.35,
      brazierMultiplier: 1.0,
      restMultiplier: 0.7,
      chestMultiplier: 1.0,
      chestFlatBonus: 0,
      simpleChestCount: 1,
      loreMultiplier: 1.0,
      shopMultiplier: 1.0,
      lockedDoorMultiplier: 1.0,
      lockedDoorFlatBonus: 0,
      storylineMultiplier: 0.8,
    },
  },

  {
  id: 'corrupted',
  name: 'Corrupted Floor',
  weight: 1,
  tilesetId: 'corrupted',

  mapSize: { mode: 'range', min: 25, max: 35 },

  // Refined design:
  // start at normal torch duration,
  // then clamp to dominanceTorchDuration at 70%+ corruption.
  torchDuration: {
    mode: 'default',
    value: DEFAULT_MOOD_TORCH_DURATION,
  },

  layout: {
    preset: 'default',
    forceSingleRoom: false,
    noCorridors: false,
    roomSizeMultiplier: 1.0,
    roomCountMultiplier: 1.0,
    corridorDensityMultiplier: 1.0,
    corridorLengthMultiplier: 1.0,
    deadEndMultiplier: 1.0,
  },

  counts: {
    enemyMultiplier: 1.3,
    trapMultiplier: 1.0,
    brazierMultiplier: 1.0,
    restMultiplier: 1.0,
    chestMultiplier: 1.0,
    chestFlatBonus: 0,
    simpleChestCount: 1,
    loreMultiplier: 1.0,
    shopMultiplier: 1.0,
    lockedDoorMultiplier: 1.0,
    lockedDoorFlatBonus: 0,
    storylineMultiplier: 0.7,
  },

  corruption: {
    initialCoverage: 0.55,

    phase2Threshold: 0.65,
    collapseThreshold: 1.0,

    spreadEveryActions: 3,
    spreadEveryActionsAfterEnemiesCleared: 10,
    spreadEveryActionsDominance: 3,

    pauseIfAllBraziersLit: true,
    cleanseLightTurnsRequired: 6,

    dominanceTorchDuration: 48,

    specialEnemySpawnChanceMin: 0.45,
    specialEnemySpawnChanceMax: 0.65,

    clusterSeedCountMin: 2,
    clusterSeedCountMax: 5,

    preferTwoNeighborWeight: 2.5,
    jumpSpreadChance: 0.18,

    avoidSpawnRadius: 4,
  },
},

  {
  id: 'normal',
  name: 'Normal Floor',
  weight: 2.5,
  tilesetId: 'default',

  mapSize: { mode: 'range', min: 25, max: 35 },

  torchDuration: {
    mode: 'default',
    value: DEFAULT_MOOD_TORCH_DURATION,
  },

  layout: {
    preset: 'default',
    forceSingleRoom: false,
    noCorridors: false,
    roomSizeMultiplier: 1.0,
    roomCountMultiplier: 1.0,
    corridorDensityMultiplier: 1.0,
    corridorLengthMultiplier: 1.0,
    deadEndMultiplier: 1.0,
  },

  counts: {
    enemyMultiplier: 1.0,
    trapMultiplier: 1.0,
    brazierMultiplier: 1.0,
    restMultiplier: 1.0,
    chestMultiplier: 1.0,
    chestFlatBonus: 0,
    simpleChestCount: 1,
    loreMultiplier: 1.0,
    shopMultiplier: 1.0,
    lockedDoorMultiplier: 1.0,
    lockedDoorFlatBonus: 0,
    storylineMultiplier: 1.0,
  },
},

  {
    id: 'sanctified',
    name: 'Sanctified Floor',
    weight: 3,
    tilesetId: 'default',

    mapSize: { mode: 'range', min: 25, max: 35 },

    torchDuration: {
      mode: 'default',
      value: DEFAULT_MOOD_TORCH_DURATION,
    },

    layout: {
      preset: 'default',
      forceSingleRoom: false,
      noCorridors: false,
      roomSizeMultiplier: 1.0,
      roomCountMultiplier: 1.0,
      corridorDensityMultiplier: 1.0,
      corridorLengthMultiplier: 1.0,
      deadEndMultiplier: 1.0,
    },

    counts: {
      enemyMultiplier: 0.65,
      trapMultiplier: 1.0,
      brazierMultiplier: 1.4,
      restMultiplier: 1.35,
      chestMultiplier: 1.0,
      chestFlatBonus: 1,
      simpleChestCount: 1,
      loreMultiplier: 1.5,
      shopMultiplier: 1.35,
      lockedDoorMultiplier: 1.0,
      lockedDoorFlatBonus: 0,
      storylineMultiplier: 1.5,
      statueMultiplier: 1.4,
      statueFlatBonus: 2,
      statueSpawnChance: 1,
    },
  },

  {
    id: 'populated',
    name: 'Populated Floor',
    weight: 2,
    tilesetId: 'default',
    merchantStockMode: 'per-merchant',

    mapSize: { mode: 'range', min: 25, max: 35 },

    torchDuration: {
      mode: 'default',
      value: DEFAULT_MOOD_TORCH_DURATION,
    },

    layout: {
      preset: 'default',
      forceSingleRoom: false,
      noCorridors: false,
      roomSizeMultiplier: 1.0,
      roomCountMultiplier: 1.0,
      corridorDensityMultiplier: 1.0,
      corridorLengthMultiplier: 1.0,
      deadEndMultiplier: 1.0,
    },

    counts: {
      enemyMultiplier: 0.75,
      trapMultiplier: 0.7,
      brazierMultiplier: 1.0,
      restMultiplier: 1.0,
      chestMultiplier: 1.0,
      chestFlatBonus: 1,
      simpleChestCount: 1,
      loreMultiplier: 1.0,
      shopMultiplier: 2.2,
      lockedDoorMultiplier: 1.3,
      lockedDoorFlatBonus: 2,
      storylineMultiplier: 1.6,
    },
  },

  {
    id: 'colossal',
    name: 'Colossal Floor',
    weight: 1,
    tilesetId: 'default',

    mapSize: { mode: 'fixed', value: 25 },

    torchDuration: {
      mode: 'default',
      value: DEFAULT_MOOD_TORCH_DURATION,
    },

    layout: {
      preset: 'colossal',
      forceSingleRoom: true,
      noCorridors: true,
      roomSizeMultiplier: 2.2,
      roomCountMultiplier: 0.15,
      corridorDensityMultiplier: 0.0,
      corridorLengthMultiplier: 0.0,
      deadEndMultiplier: 0.0,
    },

    counts: {
      enemyMultiplier: 1.3,
      trapMultiplier: 1.35,
      brazierMultiplier: 1.3,
      restMultiplier: 1.2,
      chestMultiplier: 1.35,
      chestFlatBonus: 2,
      simpleChestCount: 1,
      loreMultiplier: 1.35,
      shopMultiplier: 1.10,
      lockedDoorMultiplier: 1.0,
      lockedDoorFlatBonus: 0,
      storylineMultiplier: 1.8,
    },
  },

  {
    id: 'labyrinthian',
    name: 'Labyrinthian Floor',
    weight: 1,
    tilesetId: 'default',

    mapSize: { mode: 'range', min: 20, max: 25 },

    torchDuration: {
      mode: 'default',
      value: DEFAULT_MOOD_TORCH_DURATION,
    },

    layout: {
      preset: 'labyrinthian',
      forceSingleRoom: false,
      noCorridors: false,
      roomSizeMultiplier: 0.65,
      roomCountMultiplier: 1.6,
      corridorDensityMultiplier: 0.35,
      corridorLengthMultiplier: 1.5,
      deadEndMultiplier: 2.8,
    },

    counts: {
      enemyMultiplier: 0.75,
      trapMultiplier: 1.25,
      brazierMultiplier: 1.0,
      restMultiplier: 1.0,
      chestMultiplier: 1.0,
      chestFlatBonus: 2,
      simpleChestCount: 1,
      loreMultiplier: 1.0,
      shopMultiplier: 1.0,
      lockedDoorMultiplier: 1.0,
      lockedDoorFlatBonus: 0,
      storylineMultiplier: 1.2,
    },
  },
];

/* =============================================
 * LOOKUPS
 * ============================================= */

export const FLOOR_MOOD_BY_ID = Object.fromEntries(
  FLOOR_MOODS.map((mood) => [mood.id, mood])
);

export const DEFAULT_FLOOR_MOOD_ID = FLOOR_MOODS[0]?.id || 'overtaken';

export function getFloorMoodById(moodId) {
  const normalized = normalizeMoodId(moodId);
  return FLOOR_MOOD_BY_ID[normalized] || FLOOR_MOOD_BY_ID[DEFAULT_FLOOR_MOOD_ID];
}

export function getAllFloorMoods() {
  return [...FLOOR_MOODS];
}

/* =============================================
 * DETERMINISTIC MOOD PICKING
 * ============================================= */

/**
 * Picks one mood deterministically from runSeed + floor.
 * Same runSeed + same floor => same mood.
 */
export function pickFloorMood({ floor = 1, runSeed = 0 } = {}) {
  const safeFloor = Math.max(1, Math.floor(Number(floor) || 1));
  const safeSeed = (Number(runSeed) || 0) >>> 0;

  const mixedSeed = (
    safeSeed ^
    Math.imul(safeFloor, 0x9E3779B1) ^
    0xA341316C
  ) >>> 0;

  const rng = mulberry32(mixedSeed);

  const totalWeight = FLOOR_MOODS.reduce((sum, mood) => {
    return sum + Math.max(0, Number(mood.weight) || 0);
  }, 0);

  if (totalWeight <= 0) {
    return getFloorMoodById(DEFAULT_FLOOR_MOOD_ID);
  }

  let roll = rng() * totalWeight;

  for (const mood of FLOOR_MOODS) {
    roll -= Math.max(0, Number(mood.weight) || 0);
    if (roll <= 0) return mood;
  }

  return FLOOR_MOODS[FLOOR_MOODS.length - 1];
}

/* =============================================
 * MOOD HELPERS
 * ============================================= */

export function resolveFloorMood(input, fallbackId = DEFAULT_FLOOR_MOOD_ID) {
  if (input && typeof input === 'object' && input.id) {
    return getFloorMoodById(input.id);
  }
  if (typeof input === 'string') {
    return getFloorMoodById(input);
  }
  return getFloorMoodById(fallbackId);
}

export function getFloorMoodName(input) {
  return resolveFloorMood(input).name;
}

export function getFloorMoodTilesetId(input) {
  const mood = resolveFloorMood(input);
  return mood.tilesetId || DEFAULT_FLOOR_TILESET_ID;
}

export function getFloorMoodTorchDuration(input, fallbackDuration = DEFAULT_MOOD_TORCH_DURATION) {
  const mood = resolveFloorMood(input);

  if (!mood.torchDuration || mood.torchDuration.mode === 'default') {
    return Math.max(1, Math.floor(Number(fallbackDuration) || DEFAULT_MOOD_TORCH_DURATION));
  }

  if (mood.torchDuration.mode === 'fixed') {
    return Math.max(1, Math.floor(Number(mood.torchDuration.value) || fallbackDuration));
  }

  return Math.max(1, Math.floor(Number(fallbackDuration) || DEFAULT_MOOD_TORCH_DURATION));
}

export function rollFloorMoodMapSize(input, { floor = 1, runSeed = 0 } = {}) {
  const mood = resolveFloorMood(input);
  const safeFloor = Math.max(1, Math.floor(Number(floor) || 1));
  const safeSeed = (Number(runSeed) || 0) >>> 0;

  const mixedSeed = (
    safeSeed ^
    Math.imul(safeFloor, 0x85EBCA6B) ^
    0xC2B2AE35
  ) >>> 0;

  const rng = mulberry32(mixedSeed);

  if (!mood.mapSize) return 20;

  if (mood.mapSize.mode === 'fixed') {
    return Math.max(5, Math.floor(Number(mood.mapSize.value) || 20));
  }

  if (mood.mapSize.mode === 'range') {
    const min = Math.max(5, Math.floor(Number(mood.mapSize.min) || 20));
    const max = Math.max(min, Math.floor(Number(mood.mapSize.max) || min));
    return clamp(min + Math.floor(rng() * (max - min + 1)), min, max);
  }

  return 20;
}

export function getFloorMoodGenerationProfile(input) {
  const mood = resolveFloorMood(input);

  return {
    id: mood.id,
    name: mood.name,
    tilesetId: mood.tilesetId || DEFAULT_FLOOR_TILESET_ID,
    layout: {
      preset: mood.layout?.preset || 'default',
      forceSingleRoom: !!mood.layout?.forceSingleRoom,
      noCorridors: !!mood.layout?.noCorridors,
      roomSizeMultiplier: Number(mood.layout?.roomSizeMultiplier ?? 1),
      roomCountMultiplier: Number(mood.layout?.roomCountMultiplier ?? 1),
      corridorDensityMultiplier: Number(mood.layout?.corridorDensityMultiplier ?? 1),
      corridorLengthMultiplier: Number(mood.layout?.corridorLengthMultiplier ?? 1),
      deadEndMultiplier: Number(mood.layout?.deadEndMultiplier ?? 1),
    },
    counts: {
      enemyMultiplier: Number(mood.counts?.enemyMultiplier ?? 1),
      trapMultiplier: Number(mood.counts?.trapMultiplier ?? 1),
      brazierMultiplier: Number(mood.counts?.brazierMultiplier ?? 1),
      restMultiplier: Number(mood.counts?.restMultiplier ?? 1),
      chestMultiplier: Number(mood.counts?.chestMultiplier ?? 1),
      chestFlatBonus: Number(mood.counts?.chestFlatBonus ?? 0),
      simpleChestCount: Math.floor(Number(mood.counts?.simpleChestCount ?? 1)),
      loreMultiplier: Number(mood.counts?.loreMultiplier ?? 1),
      shopMultiplier: Number(mood.counts?.shopMultiplier ?? 1),
      lockedDoorMultiplier: Number(mood.counts?.lockedDoorMultiplier ?? 1),
      lockedDoorFlatBonus: Number(mood.counts?.lockedDoorFlatBonus ?? 0),
      storylineMultiplier: Number(mood.counts?.storylineMultiplier ?? 1),
      statueMultiplier: Number(mood.counts?.statueMultiplier ?? 0),
      statueFlatBonus: Number(mood.counts?.statueFlatBonus ?? 0),
      statueSpawnChance: Number(mood.counts?.statueSpawnChance ?? 1),
    },
      corruption: {
      ...(mood.corruption || {}),
    },
  };
}